import React, { useEffect, useState } from 'react';
import UserAvatar from '../ui/UserAvatar';
import organizationsService from '../../services/organizationsService';

// ── Activity ─────────────────────────────────────────────────────────────────

/**
 * Human sentences for the audit log's action strings.
 *
 * A lookup rather than a switch, and an UNKNOWN action falls back to the raw
 * string rather than being hidden. The server deliberately keeps `action` a
 * free string so a new event never needs a migration — a UI that dropped what
 * it did not recognise would make the log quietly incomplete, which is the one
 * thing an audit log may not be.
 */
export const AUDIT_LABELS = {
  'member.added': 'added a member',
  'member.removed': 'removed a member',
  'member.left': 'left the organization',
  'member.role_changed': 'changed a role',
  'team.attached': 'attached a team',
  'team.detached': 'detached a team',
  'domain.claimed': 'claimed a domain',
  'domain.verified': 'verified a domain',
  'domain.removed': 'removed a domain',
  'invitation.sent': 'invited someone',
  'invitation.revoked': 'revoked an invitation',
  'invitation.accepted': 'accepted an invitation',
  'organization.updated': 'updated the organization',
};

export function auditDetail(entry) {
  const m = entry.metadata || {};
  if (entry.action === 'member.role_changed' && m.from && m.to) return `${m.from} → ${m.to}`;
  if (m.domain) return m.domain;
  if (m.name) return m.name;
  if (m.role) return m.role;
  // Field NAMES, never values — the server sends only names for exactly this.
  if (Array.isArray(m.fields)) return m.fields.join(', ');
  return null;
}

export function AuditTab({ org }) {
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await organizationsService.getAudit(org.id);
        if (!cancelled) setEntries(rows);
      } catch (e) {
        // Owner/admin only server-side; a plain member gets a message rather
        // than an empty list that looks like "nothing ever happened".
        if (!cancelled) setError(e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [org.id]);

  if (error) return <p className="org-hint">{error}</p>;
  if (entries.length === 0) return <p className="org-hint">Nothing recorded yet.</p>;

  return (
    <div className="org-list">
      {entries.map((e) => {
        const detail = auditDetail(e);
        return (
          <div key={e.id} className="org-row">
            <UserAvatar userId={e.actor_id} name={e.actor_email} size={26} className="org-row-avatar" />
            <div className="org-row-texts">
              <span className="org-row-name">
                {/* The email, not the id — and it survives the account being
                    deleted, which is when this line matters most. */}
                <strong>{e.actor_email || 'Someone'}</strong> {AUDIT_LABELS[e.action] || e.action}
                {detail && <span className="org-audit-detail"> · {detail}</span>}
              </span>
              <span className="org-row-sub">{new Date(e.inserted_at).toLocaleString()}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
